import React, { useState, useEffect, useRef, useContext } from "react";
import { GameContext } from "@/contexts/GameContext";
import { X, Sparkles, MessageSquare, Heart, HelpCircle, Compass, Cookie, RefreshCw, Move } from "lucide-react";

const NYX_QUOTES = {
  it: {
    idle: [
      "Le storie sono infinite. Andiamo a scriverne una insieme?",
      "Hai controllato la tua Sferografia oggi? Ci sono nodi e sentieri ibridi da esplorare!",
      "Un passo alla volta, avventuriero. La magia richiede tempo e saggezza.",
      "Sento una forte energia arcana nell'aria...",
      "Cosa succederà nel prossimo capitolo? Non vedo l'ora di scoprirlo!",
      "Ricorda che puoi trascinarmi in qualsiasi punto dello schermo per non coprirti la visuale!",
      "Il Cuore del Primordio nella Sferografia richiede 10 punti, ma dona bonus leggendari!"
    ],
    sphereGrid: [
      "Hai Punti Sfera disponibili! Apri la Sferografia per potenziare i tuoi attributi!",
      "I nodi Keystone richiedono 3 Punti Sfera e sbloccano talenti passivi unici.",
      "I ponti ibridi tra le classi ti permettono di creare combinazioni devastanti!"
    ],
    lowHp: [
      "Attenzione! La tua salute è bassa! Dovresti usare una pozione di cura!",
      "PV critici! Non vorrai farti sconfiggere proprio adesso, vero? Proteggiti subito!",
      "Presto, difenditi! Il prossimo colpo potrebbe essere letale."
    ],
    battle: [
      "Canalizza la tua magia! Sconfiggiamo questo nemico!",
      "Usa le combo giuste per infliggere più danni!",
      "Mantieni alta la difesa! La parata è fondamentale quanto l'attacco.",
      "Sento il vento della vittoria soffiare a nostro favore!"
    ],
    highGold: [
      "Che bel bottino d'oro! Facciamo un salto al Negozio per comprare qualche carta o pozione?",
      "Tutto quest'oro fa quasi invidia a un drago! Usalo per potenziarti o fare un respec!"
    ],
    welcome: [
      "Ciao! Sono Nix, la tua Guida Arcana. Puoi trascinarmi dove preferisci!",
      "Che la fortuna ti assista in questo viaggio magico!"
    ],
    jokes: [
      "Perché i maghi non giocano a nascondino? Perché si fanno sempre scoprire con la chiaroveggenza!",
      "Sai qual è il cibo preferito di un drago? I cavalieri in scatola (con l'armatura)!",
      "Ho provato a lanciare un incantesimo d'invisibilità, ma ora non trovo più le mie chiavi!"
    ]
  },
  en: {
    idle: [
      "Stories are infinite. Shall we go write one together?",
      "Have you checked your Sphere Grid today? There are nodes and hybrid paths to explore!",
      "One step at a time, adventurer. Magic takes time and wisdom.",
      "I feel a strong arcane energy in the air...",
      "What will happen in the next chapter? I can't wait to find out!",
      "Remember you can drag me anywhere across the screen so I won't block your view!",
      "The Primordial Core in the Sphere Grid costs 10 points, but grants legendary power!"
    ],
    sphereGrid: [
      "You have Sphere Points available! Open the Sphere Grid to boost your attributes!",
      "Keystone nodes require 3 Sphere Points and unlock unique passive traits.",
      "Hybrid bridges between classes allow you to craft game-changing synergies!"
    ],
    lowHp: [
      "Watch out! Your health is low! You should use a healing potion!",
      "Critical HP! You don't want to get defeated right now, do you? Defend yourself!",
      "Quick, protect yourself! The next hit could be fatal."
    ],
    battle: [
      "Channel your magic! Let's defeat this enemy!",
      "Use the right combos to deal massive damage!",
      "Keep your defense high! Blocking is as crucial as attacking.",
      "I feel the winds of victory blowing in our favor!"
    ],
    highGold: [
      "What a nice hoard of gold! Shall we pay a visit to the Shop for cards or potions?",
      "All this gold makes dragons jealous! Use it to empower yourself or respec!"
    ],
    welcome: [
      "Hello! I am Nix, your Arcane Guide. You can drag me anywhere you like!",
      "May fortune favor you on this magical adventure!"
    ],
    jokes: [
      "Why don't wizards play hide and seek? Because they're always spotted with clairvoyance!",
      "What is a dragon's favorite meal? Canned knights (in full plate armor)!",
      "I tried casting an invisibility spell, but now I can't find my keys!"
    ]
  }
};

const PET_REACTIONS = {
  it: [
    "Fufufu... Grazie, avventuriero! La tua gentilezza riscalda il mio nucleo magico.",
    "Che sensazione piacevole! Sento le mie ali ricaricarsi d'energia.",
    "Le carezze di un grande eroe sono sempre gradite!",
    "Hihi! Questo solletica un po', ma mi piace un sacco!"
  ],
  en: [
    "Fufufu... Thank you, adventurer! Your kindness warms my magical core.",
    "What a pleasant feeling! I feel my wings charging up with energy.",
    "A great hero's pets are always welcome!",
    "Hehe! That tickles a bit, but I love it!"
  ]
};

const SNACK_REACTIONS = {
  it: [
    "Gnam! Delizioso! Questo dolcetto stellare mi ricarica completamente di mana!",
    "Mmm... che bontà arcana! Sei il miglior compagno di viaggio che potessi desiderare!",
    "Slurp! Grazie mille per lo spuntino magico! Ora sono pieno di energia!"
  ],
  en: [
    "Yum! Delicious! This starlight treat completely recharges my mana!",
    "Mmm... such arcane goodness! You're the best companion I could ask for!",
    "Slurp! Thank you so much for the magical snack! I'm bursting with energy now!"
  ]
};

const RIDDLES = {
  it: [
    {
      question: "Canalizzo poteri arcani, ma senza mana sono solo carta dipinta. Cosa sono?",
      options: ["Una pergamena", "Una carta incantesimo", "Un tomo di magia"],
      answer: 1,
      rewardText: "Esatto! Le carte consumano il tuo mana prezioso per compiere prodigi."
    },
    {
      question: "Rinasco dalle mie stesse ceneri per difenderti dal male. Chi sono?",
      options: ["Il Cucciolo di Fenice", "Un Golem di Fango", "L'Eroe Immortale"],
      answer: 0,
      rewardText: "Corretto! La Fenice risorge splendente e pronta a combattere."
    },
    {
      question: "Consumo la tua mente per scagliare incantesimi. Senza di me, la tua magia tace. Cosa sono?",
      options: ["L'Oro", "I Punti Vita", "Il Mana"],
      answer: 2,
      rewardText: "Giusto! Il Mana è la linfa vitale di ogni incantatore."
    },
    {
      question: "Più ne sblocchi nella Sferografia, più diventi potente. Cosa sono?",
      options: ["I Nodi Celesti", "I Forzieri Chiusi", "I Danni Subiti"],
      answer: 0,
      rewardText: "Splendido! I nodi celesti aumentano statistiche e abilità."
    }
  ],
  en: [
    {
      question: "I channel arcane powers, but without mana I am just painted paper. What am I?",
      options: ["A scroll", "A spell card", "A magic tome"],
      answer: 1,
      rewardText: "Exactly! Cards consume your precious mana to perform wonders."
    },
    {
      question: "I rise from my own ashes to defend you from evil. Who am I?",
      options: ["The Phoenix Hatchling", "A Mud Golem", "The Deathless Hero"],
      answer: 0,
      rewardText: "Correct! The Phoenix rises radiant and ready to fight."
    },
    {
      question: "I consume your mind to cast spells. Without me, your magic is silent. What am I?",
      options: ["Gold", "Hit Points", "Mana"],
      answer: 2,
      rewardText: "Right! Mana is the lifeblood of every spellcaster."
    },
    {
      question: "The more you unlock in the Sphere Grid, the mightier you become. What are they?",
      options: ["Celestial Nodes", "Locked Chests", "Received Wounds"],
      answer: 0,
      rewardText: "Wonderful! Celestial nodes empower your stats and skills."
    }
  ]
};

const TAROT_CARDS = {
  it: [
    { name: "Il Mago", icon: "✨", desc: "La tua energia arcana è all'apice. Oggi i tuoi incantesimi risuoneranno con forza immensa." },
    { name: "La Stella", icon: "⭐", desc: "Un sentiero luminoso si svela tra le ombre. Troverai la via per superare ogni ostacolo." },
    { name: "La Torre", icon: "🏰", desc: "Vecchie certezze crollano per far spazio a nuove opportunità. Il cambiamento è inevitabile." },
    { name: "Il Drago", icon: "🐉", desc: "Una brama di ricchezza pervade la giornata. Oro e tesori ti attendono, ma attento alle fiamme." },
    { name: "La Luna", icon: "🌙", desc: "Le apparenze ingannano. Guarda oltre le ombre per scoprire la verità celata nei segreti." },
    { name: "Il Sole", icon: "☀️", desc: "Chiarezza e vitalità ripristinano le tue forze. I tuoi punti vita e il tuo spirito risplendono." }
  ],
  en: [
    { name: "The Magician", icon: "✨", desc: "Your arcane energy is at its peak. Today your spells will resonate with immense power." },
    { name: "The Star", icon: "⭐", desc: "A bright path reveals itself in the shadows. You will find the way to overcome any obstacle." },
    { name: "The Tower", icon: "🏰", desc: "Old certainties crumble to make room for new opportunities. Change is inevitable." },
    { name: "The Dragon", icon: "🐉", desc: "A hunger for wealth fills the day. Gold and treasures await you, but watch out for flames." },
    { name: "The Moon", icon: "🌙", desc: "Appearances can be deceiving. Look beyond the shadows to discover the truth hidden in secrets." },
    { name: "The Sun", icon: "☀️", desc: "Clarity and vitality restore your strength. Your health and your spirit shine bright." }
  ]
};

const LORE_SECRETS = {
  it: [
    "Sapevi che il Cucciolo di Fenice è nato dal fuoco primordiale e rinasce con più forza ogni volta che viene sconfitto?",
    "I nodi Keystone della Sferografia richiedono 3 punti ciascuno e donano talenti passivi unici come Lifesteal e Contrattacco.",
    "Il Cuore del Primordio concede +150 HP, +50 Mana, +25 Attacco e +15 Difesa con 10 Punti Sfera!",
    "Combinare rami adiacenti o diagonali sblocca ponti ibridi speciali come lo Spellblade, il Teurgo o il Cavaliere della Morte."
  ],
  en: [
    "Did you know that the Phoenix Hatchling was born from primordial fire and rises stronger every time it is defeated?",
    "Keystone nodes in the Sphere Grid cost 3 points each and grant unique passive traits like Lifesteal and Counters.",
    "The Primordial Core grants +150 HP, +50 Mana, +25 Attack, and +15 Defense for 10 Sphere Points!",
    "Bridging adjacent or diagonal branches unlocks specialized hybrid classes like Spellblade, Theurge, or Death Knight."
  ]
};

const EXPRESSIONS = {
  idle: "/assets/mascot/nyx_idle.png?v=2",
  happy: "/assets/mascot/nyx_happy.png?v=2",
  playful: "/assets/mascot/nyx_playful.png?v=2",
  curious: "/assets/mascot/nyx_curious.png?v=2",
  determined: "/assets/mascot/nyx_determined.png?v=2"
};

// Web Audio API Synthesizer to create premium UI audio indicators
const playSynthSound = (type = "chime") => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    
    if (type === "chime") {
      const notes = [523.25, 659.25, 783.99, 1046.50];
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.08);
        gain.gain.setValueAtTime(0.08, ctx.currentTime + index * 0.08);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.08 + 0.4);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.08);
        osc.stop(ctx.currentTime + index * 0.08 + 0.5);
      });
    } else if (type === "levelup") {
      const notes = [261.63, 329.63, 392.00, 523.25, 659.25, 783.99, 1046.50];
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.06);
        gain.gain.setValueAtTime(0.12, ctx.currentTime + index * 0.06);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.06 + 1.0);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.06);
        osc.stop(ctx.currentTime + index * 0.06 + 1.2);
      });
    } else if (type === "purr") {
      const notes = [329.63, 349.23, 329.63];
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.1);
        gain.gain.setValueAtTime(0.06, ctx.currentTime + index * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.1 + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.1);
        osc.stop(ctx.currentTime + index * 0.1 + 0.4);
      });
    } else if (type === "success") {
      const notes = [523.25, 783.99];
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.1);
        gain.gain.setValueAtTime(0.1, ctx.currentTime + index * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.1 + 0.3);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.1);
        osc.stop(ctx.currentTime + index * 0.1 + 0.4);
      });
    }
  } catch (e) {
    console.warn("Web Audio API warning: ", e);
  }
};

export default function NyxMascot({ forceLang }) {
  const gameCtx = useContext(GameContext);
  const game = gameCtx?.game;

  // Visibility from Settings
  const [isVisible, setIsVisible] = useState(() => {
    return localStorage.getItem("ac_show_nix_mascot") !== "false";
  });

  useEffect(() => {
    const handleVisChange = (e) => {
      if (e.detail?.visible !== undefined) {
        setIsVisible(e.detail.visible);
      } else {
        setIsVisible(localStorage.getItem("ac_show_nix_mascot") !== "false");
      }
    };
    window.addEventListener("ac:mascot-visibility-changed", handleVisChange);
    window.addEventListener("storage", handleVisChange);
    return () => {
      window.removeEventListener("ac:mascot-visibility-changed", handleVisChange);
      window.removeEventListener("storage", handleVisChange);
    };
  }, []);

  // Visual/Expression States
  const [expression, setExpression] = useState("idle");
  const [bubbleText, setBubbleText] = useState("");
  const [showBubble, setShowBubble] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Interaction States
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState("main"); // "main" | "riddle" | "oracle" | "secrets" | "snack"

  // Draggable Positioning State
  const [customPos, setCustomPos] = useState(() => {
    try {
      const saved = localStorage.getItem("nyx_custom_pos");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, startLeft: 0, startTop: 0 });
  const hasMovedRef = useRef(false);
  const containerRef = useRef(null);

  // Affinity States (persisted)
  const [affinityLevel, setAffinityLevel] = useState(() => {
    return parseInt(localStorage.getItem("nyx_affinity_level") || "1", 10);
  });
  const [affinityExp, setAffinityExp] = useState(() => {
    return parseInt(localStorage.getItem("nyx_affinity_exp") || "0", 10);
  });

  // Tap counter for easter eggs
  const tapCountRef = useRef(0);
  const lastTapTimeRef = useRef(0);

  // Physical mascot animations
  const [isSpinning, setIsSpinning] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  // Particles system state
  const [particles, setParticles] = useState([]);

  // Riddle sub-states
  const [currentRiddle, setCurrentRiddle] = useState(0);
  const [riddleSolved, setRiddleSolved] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  // Oracle sub-states
  const [drawnCardId, setDrawnCardId] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);

  // Inactivity State
  const [isUserInactive, setIsUserInactive] = useState(false);
  const lastActivityTimeRef = useRef(Date.now());

  const lastHpRef = useRef(game?.character?.hp || 0);
  const lastLevelRef = useRef(game?.character?.level || 0);
  const bubbleTimeoutRef = useRef(null);

  const getUiLang = () => {
    if (forceLang) return forceLang.startsWith("it") ? "it" : "en";
    if (game?.language) return game.language.startsWith("it") ? "it" : "en";
    const storedUi = localStorage.getItem("ac_ui_lang");
    if (storedUi) return storedUi.startsWith("it") ? "it" : "en";
    const storedLanding = localStorage.getItem("ac_lang");
    if (storedLanding) return storedLanding.startsWith("it") ? "it" : "en";
    return "it";
  };
  const lang = getUiLang();
  const quotes = NYX_QUOTES[lang] || NYX_QUOTES.it;

  const getXpNeeded = (lvl) => lvl * 120;
  const xpNeeded = getXpNeeded(affinityLevel);
  const drawnCard = drawnCardId !== null ? (TAROT_CARDS[lang] || TAROT_CARDS.it)[drawnCardId] : null;

  // Speak Bubble Helper
  const speak = (text, exp = "idle", duration = 6000) => {
    if (bubbleTimeoutRef.current) {
      clearTimeout(bubbleTimeoutRef.current);
    }
    setBubbleText(text);
    setExpression(exp);
    setShowBubble(true);

    bubbleTimeoutRef.current = setTimeout(() => {
      setShowBubble(false);
      setExpression("idle");
    }, duration);
  };

  // Particle Spawner
  const spawnParticles = (type, count = 10) => {
    const list = [];
    const colors = type === "heart" 
      ? ["#ef4444", "#f43f5e", "#ec4899", "#d946ef"] 
      : ["#cfb53b", "#e5cc5b", "#38bdf8", "#a855f7", "#10b981"];
    
    for (let i = 0; i < count; i++) {
      list.push({
        id: Math.random().toString(),
        x: Math.random() * 60 - 30,
        y: Math.random() * 40 - 20,
        vx: (Math.random() - 0.5) * 3,
        vy: -Math.random() * 3 - 2.5,
        type: type === "heart" ? "❤️" : type === "cookie" ? "🍪" : Math.random() > 0.5 ? "✨" : "⭐",
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 10 + 10,
        opacity: 1,
        scale: 1,
        rotation: Math.random() * 360
      });
    }
    setParticles(prev => [...prev, ...list]);
  };

  // Particle physics update
  useEffect(() => {
    if (particles.length === 0) return;
    const interval = setInterval(() => {
      setParticles(prev => 
        prev
          .map(p => ({
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            vy: p.vy + 0.05,
            opacity: p.opacity - 0.04,
            scale: p.scale - 0.02,
            rotation: p.rotation + p.vx * 2
          }))
          .filter(p => p.opacity > 0)
      );
    }, 30);
    return () => clearInterval(interval);
  }, [particles]);

  // Affinity increaser helper
  const gainAffinity = (amount) => {
    setAffinityExp(prevExp => {
      let currentLvl = affinityLevel;
      let nextExp = prevExp + amount;
      let needed = getXpNeeded(currentLvl);

      if (nextExp >= needed) {
        while (nextExp >= needed) {
          nextExp -= needed;
          currentLvl += 1;
          needed = getXpNeeded(currentLvl);
        }
        
        setAffinityLevel(currentLvl);
        localStorage.setItem("nyx_affinity_level", currentLvl.toString());
        localStorage.setItem("nyx_affinity_exp", nextExp.toString());
        
        setIsJumping(true);
        setTimeout(() => setIsJumping(false), 600);
        spawnParticles("sparkle", 25);
        playSynthSound("levelup");
        
        const levelUpMsg = lang === "it"
          ? `Favoloso! Il nostro legame si è rafforzato! Livello Affinità ${currentLvl}! La mia magia risplende!`
          : `Fabulous! Our bond has strengthened! Affinity Level ${currentLvl}! My magic shines bright!`;
        speak(levelUpMsg, "happy", 7500);
        
        return nextExp;
      } else {
        localStorage.setItem("nyx_affinity_exp", nextExp.toString());
        return nextExp;
      }
    });
  };

  // Dragging Handlers
  const handlePointerDown = (e) => {
    // Only drag on left click or touch
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest("button") || e.target.closest(".nyx-interactive-card")) return;

    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };
    hasMovedRef.current = false;
    setIsDragging(true);

    const onPointerMove = (moveEvt) => {
      const dx = moveEvt.clientX - dragStartRef.current.x;
      const dy = moveEvt.clientY - dragStartRef.current.y;

      if (Math.hypot(dx, dy) > 5) {
        hasMovedRef.current = true;
      }

      let newX = dragStartRef.current.startLeft + dx;
      let newY = dragStartRef.current.startTop + dy;

      // Clamp within viewport boundaries
      const maxX = Math.max(10, window.innerWidth - 85);
      const maxY = Math.max(10, window.innerHeight - 85);
      newX = Math.min(Math.max(10, newX), maxX);
      newY = Math.min(Math.max(10, newY), maxY);

      setCustomPos({ x: newX, y: newY });
    };

    const onPointerUp = () => {
      setIsDragging(false);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);

      if (hasMovedRef.current) {
        setCustomPos((currentPos) => {
          if (currentPos) {
            localStorage.setItem("nyx_custom_pos", JSON.stringify(currentPos));
          }
          return currentPos;
        });
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  // Reset mascot position to default
  const handleResetPosition = (e) => {
    e.stopPropagation();
    localStorage.removeItem("nyx_custom_pos");
    setCustomPos(null);
    speak(lang === "it" ? "Posizione reimpostata!" : "Position reset!", "happy", 3000);
  };

  // Click & Double Click Tap Handler
  const handleMascotClick = (e) => {
    if (hasMovedRef.current) return;

    const now = Date.now();
    const timeSinceLastTap = now - lastTapTimeRef.current;
    lastTapTimeRef.current = now;

    // Double tap detected!
    if (timeSinceLastTap < 350) {
      tapCountRef.current = 0;
      setIsSpinning(true);
      setIsJumping(true);
      spawnParticles("sparkle", 18);
      playSynthSound("levelup");
      setTimeout(() => {
        setIsSpinning(false);
        setIsJumping(false);
      }, 700);

      const doubleTapQuotes = lang === "it"
        ? ["Wooo! Guarda che acrobazia arcana!", "Salto carpiato con avvitamento cosmico!", "Taa-daaa! 10 su 10 per lo stile!"]
        : ["Wooo! Check out this arcane flip!", "Cosmic somersault with a magic twist!", "Taa-daaa! 10 out of 10 for style!"];
      speak(doubleTapQuotes[Math.floor(Math.random() * doubleTapQuotes.length)], "playful", 4000);
      gainAffinity(15);
      return;
    }

    // Single Click: Easter egg tap count
    tapCountRef.current += 1;
    if (tapCountRef.current >= 5) {
      tapCountRef.current = 0;
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      spawnParticles("sparkle", 12);
      speak(lang === "it" ? "Hihihi! Mi gira un po' la testa con tutti questi tocchi veloci!" : "Hehehe! My head is spinning with all these rapid taps!", "playful", 5000);
      return;
    }

    if (isCollapsed) {
      setIsCollapsed(false);
      return;
    }

    setMenuOpen(prev => !prev);
    setActiveView("main");
    setShowBubble(false);
    playSynthSound("chime");
  };

  // Pet Action
  const handlePet = () => {
    spawnParticles("heart", 12);
    playSynthSound("purr");
    gainAffinity(12);

    const petReactionList = PET_REACTIONS[lang] || PET_REACTIONS.it;
    const reaction = petReactionList[Math.floor(Math.random() * petReactionList.length)];
    speak(reaction, "happy", 6000);
    setMenuOpen(false);
  };

  // Feed Snack Action
  const handleFeedSnack = () => {
    spawnParticles("cookie", 10);
    spawnParticles("sparkle", 10);
    playSynthSound("success");
    setIsJumping(true);
    setTimeout(() => setIsJumping(false), 600);
    gainAffinity(25);

    const snackList = SNACK_REACTIONS[lang] || SNACK_REACTIONS.it;
    const reaction = snackList[Math.floor(Math.random() * snackList.length)];
    speak(reaction, "playful", 6000);
    setMenuOpen(false);
  };

  // Advice Action
  const handleGiveAdvice = () => {
    let quote = "";
    let exp = "curious";

    const spherePoints = game?.character?.sphere_points || 0;
    const currentHp = game?.character?.hp || 0;
    const maxHp = game?.character?.max_hp || 100;
    const inBattle = !!game?.battle_state?.active;
    const gold = game?.character?.gold || 0;

    if (spherePoints > 0) {
      const spList = quotes.sphereGrid || quotes.idle;
      quote = spList[Math.floor(Math.random() * spList.length)];
      exp = "happy";
    } else if (inBattle) {
      const bList = quotes.battle;
      quote = bList[Math.floor(Math.random() * bList.length)];
      exp = "determined";
    } else if (currentHp / maxHp < 0.3 && currentHp > 0) {
      quote = lang === "it"
        ? "Attenzione, salute critica! Ti suggerisco caldamente di usare una pozione di cura!"
        : "Warning, critical health! I strongly advise using a healing potion!";
      exp = "determined";
    } else if (gold >= 500) {
      quote = lang === "it"
        ? "Che bel gruzzolo d'oro! Fai un salto al Negozio o sblocca nuovi slot al Mercato!"
        : "What a nice hoard of gold! Visit the Shop or unlock new slots in the Market!";
      exp = "playful";
    } else {
      const allQuotes = [...quotes.idle, ...(quotes.jokes || [])];
      quote = allQuotes[Math.floor(Math.random() * allQuotes.length)];
      exp = Math.random() > 0.5 ? "happy" : "curious";
    }

    speak(quote, exp, 7000);
    setMenuOpen(false);
  };

  // Inactivity Detection Hook
  useEffect(() => {
    const handleActivity = () => {
      lastActivityTimeRef.current = Date.now();
      if (isUserInactive) {
        setIsUserInactive(false);
        setExpression("idle");
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    
    const checkInterval = setInterval(() => {
      const idleTime = Date.now() - lastActivityTimeRef.current;
      if (idleTime > 45000 && !isUserInactive && !menuOpen && !showBubble && !isCollapsed) {
        setIsUserInactive(true);
        const idleText = lang === "it"
          ? "Zzz... Oh! Scusa, mi ero quasi addormentato. L'energia arcana a volte concilia il sonno... C'è qualche avventura in vista?"
          : "Zzz... Oh! Sorry, I almost dozed off. Arcane energy makes me sleepy sometimes... Any adventure in sight?";
        speak(idleText, "playful", 8000);
      }
    }, 5000);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      clearInterval(checkInterval);
    };
  }, [isUserInactive, menuOpen, showBubble, isCollapsed, lang]);

  // Contextual Game Changes
  useEffect(() => {
    if (!game || isCollapsed) return;

    const currentHp = game.character?.hp || 0;
    const maxHp = game.character?.max_hp || 100;
    const currentLevel = game.character?.level || 0;
    const inBattle = !!game.battle_state?.active;

    if (currentLevel > lastLevelRef.current && lastLevelRef.current > 0) {
      setMenuOpen(false);
      const congrats = lang === "it"
        ? `Incredibile! Sei salito al livello ${currentLevel}! Hai ottenuto un Punto Sfera per la Sferografia!`
        : `Incredible! You reached level ${currentLevel}! You earned a Sphere Point for the Sphere Grid!`;
      speak(congrats, "happy", 7000);
      lastLevelRef.current = currentLevel;
      lastHpRef.current = currentHp;
      return;
    }
    lastLevelRef.current = currentLevel;

    if (inBattle) {
      setMenuOpen(false);
      setExpression("determined");
      return;
    }

    if (currentHp / maxHp < 0.3 && currentHp > 0) {
      if (lastHpRef.current / maxHp >= 0.3) {
        setMenuOpen(false);
        const warning = quotes.lowHp[Math.floor(Math.random() * quotes.lowHp.length)];
        speak(warning, "determined", 7000);
      }
      lastHpRef.current = currentHp;
      return;
    }
    lastHpRef.current = currentHp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, isCollapsed]);

  // If disabled in settings, render nothing
  if (!isVisible) return null;

  const imageSrc = EXPRESSIONS[expression] || EXPRESSIONS.idle;

  // Compute container position style
  const positionStyle = customPos
    ? {
        position: "fixed",
        left: `${customPos.x}px`,
        top: `${customPos.y}px`,
        right: "auto",
        bottom: "auto",
        zIndex: 50,
      }
    : {
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 50,
      };

  return (
    <div
      ref={containerRef}
      style={positionStyle}
      onPointerDown={handlePointerDown}
      className={`touch-none select-none transition-transform duration-75 ${
        isDragging ? "cursor-grabbing scale-105 opacity-90 shadow-2xl" : "cursor-grab"
      }`}
    >
      {/* Speech Bubble */}
      {showBubble && !isCollapsed && (
        <div
          className={`absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-64 sm:w-72 bg-gradient-to-br from-[#1c1a24] to-[#121118] border-2 border-[#cfb53b]/80 p-3 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.8)] z-30 animate-fade-in`}
        >
          <p className="text-xs text-slate-200 font-medium leading-relaxed m-0">
            {bubbleText}
          </p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-0.5 border-solid border-t-[#cfb53b] border-t-8 border-x-transparent border-x-8 border-b-0" />
        </div>
      )}

      {/* Action Menu */}
      {menuOpen && !isCollapsed && (
        <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-64 bg-[#14121a]/95 backdrop-blur-md border border-[#cfb53b]/60 p-3 rounded-2xl shadow-2xl z-30 animate-fade-in">
          <div className="flex items-center justify-between border-b border-[#cfb53b]/20 pb-2 mb-2">
            <div className="flex items-center gap-1.5">
              <Sparkles size={14} className="text-[#cfb53b]" />
              <span className="font-heading text-xs font-bold text-[#cfb53b]">Nix (Lv. {affinityLevel})</span>
            </div>
            <div className="flex items-center gap-1">
              {customPos && (
                <button
                  onClick={handleResetPosition}
                  title={lang === "it" ? "Ripristina posizione predefinita" : "Reset position"}
                  className="text-slate-400 hover:text-yellow-300 p-0.5"
                >
                  <Move size={12} />
                </button>
              )}
              <button
                onClick={() => setMenuOpen(false)}
                className="text-slate-400 hover:text-white p-0.5"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {activeView === "main" && (
            <div className="space-y-1.5">
              <button
                onClick={handleGiveAdvice}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-white/5 hover:bg-[#cfb53b]/20 rounded-lg text-left text-xs text-slate-200 transition-colors"
              >
                <MessageSquare size={13} className="text-yellow-400" />
                <span>{lang === "it" ? "Chiedi un consiglio o trucco" : "Ask for advice or tips"}</span>
              </button>

              <button
                onClick={handlePet}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-white/5 hover:bg-pink-500/20 rounded-lg text-left text-xs text-slate-200 transition-colors"
              >
                <Heart size={13} className="text-pink-400" />
                <span>{lang === "it" ? "Accarezza Nix (+12 XP)" : "Pet Nix (+12 XP)"}</span>
              </button>

              <button
                onClick={handleFeedSnack}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-white/5 hover:bg-amber-500/20 rounded-lg text-left text-xs text-slate-200 transition-colors"
              >
                <Cookie size={13} className="text-amber-400" />
                <span>{lang === "it" ? "Offri Spuntino Magico (+25 XP)" : "Feed Magic Snack (+25 XP)"}</span>
              </button>

              <button
                onClick={() => {
                  const rList = RIDDLES[lang] || RIDDLES.it;
                  setCurrentRiddle(Math.floor(Math.random() * rList.length));
                  setRiddleSolved(null);
                  setSelectedOption(null);
                  setActiveView("riddle");
                }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-white/5 hover:bg-purple-500/20 rounded-lg text-left text-xs text-slate-200 transition-colors"
              >
                <HelpCircle size={13} className="text-purple-400" />
                <span>{lang === "it" ? "Sfida d'Indovinello" : "Riddle Challenge"}</span>
              </button>

              <button
                onClick={() => {
                  const tList = TAROT_CARDS[lang] || TAROT_CARDS.it;
                  setDrawnCardId(Math.floor(Math.random() * tList.length));
                  setIsFlipping(true);
                  setActiveView("oracle");
                }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-white/5 hover:bg-cyan-500/20 rounded-lg text-left text-xs text-slate-200 transition-colors"
              >
                <Compass size={13} className="text-cyan-400" />
                <span>{lang === "it" ? "Oracolo Astrale del Giorno" : "Daily Astral Oracle"}</span>
              </button>

              {/* Drag Tip */}
              <p className="text-[9px] text-center text-slate-500 pt-1">
                {lang === "it" ? "💡 Trascina Nix ovunque sullo schermo!" : "💡 Drag Nix anywhere across the screen!"}
              </p>
            </div>
          )}

          {activeView === "riddle" && (
            <div className="space-y-2">
              {(() => {
                const rList = RIDDLES[lang] || RIDDLES.it;
                const r = rList[currentRiddle];
                return (
                  <>
                    <p className="text-xs text-purple-200 font-medium">{r.question}</p>
                    <div className="space-y-1">
                      {r.options.map((opt, idx) => (
                        <button
                          key={idx}
                          disabled={riddleSolved !== null}
                          onClick={() => {
                            setSelectedOption(idx);
                            if (idx === r.answer) {
                              setRiddleSolved("correct");
                              spawnParticles("sparkle", 15);
                              playSynthSound("success");
                              gainAffinity(30);
                            } else {
                              setRiddleSolved("wrong");
                              playSynthSound("error");
                            }
                          }}
                          className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${
                            riddleSolved !== null
                              ? idx === r.answer
                                ? "bg-emerald-900/60 text-emerald-200 border border-emerald-500/50"
                                : idx === selectedOption
                                ? "bg-rose-900/60 text-rose-200 border border-rose-500/50"
                                : "bg-white/5 text-slate-400"
                              : "bg-white/10 hover:bg-white/20 text-slate-200"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                    {riddleSolved === "correct" && (
                      <p className="text-[11px] text-emerald-300 font-bold">{r.rewardText} (+30 XP!)</p>
                    )}
                    {riddleSolved === "wrong" && (
                      <p className="text-[11px] text-rose-300">Non proprio! Riprova la prossima volta.</p>
                    )}
                    <button
                      onClick={() => setActiveView("main")}
                      className="text-[10px] text-slate-400 hover:text-white underline pt-1 block"
                    >
                      {lang === "it" ? "← Torna al menu" : "← Back to menu"}
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {activeView === "oracle" && drawnCard && (
            <div className="space-y-2 text-center">
              <div className="text-3xl my-1 animate-bounce">{drawnCard.icon}</div>
              <h4 className="font-heading text-xs font-bold text-yellow-300">{drawnCard.name}</h4>
              <p className="text-[11px] text-slate-300 leading-relaxed">{drawnCard.desc}</p>
              <button
                onClick={() => setActiveView("main")}
                className="text-[10px] text-slate-400 hover:text-white underline pt-1 block mx-auto"
              >
                {lang === "it" ? "← Torna al menu" : "← Back to menu"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mascot Main Visual */}
      <div
        onClick={handleMascotClick}
        className={`relative w-16 h-16 sm:w-20 sm:h-20 transition-all duration-300 ${
          isSpinning ? "animate-nyx-spin" : ""
        } ${isJumping ? "animate-nyx-jump" : ""} ${isShaking ? "animate-nyx-shake" : ""} ${
          !isDragging && !isSpinning && !isJumping ? "animate-nyx-float" : ""
        }`}
      >
        {/* Affinity Level Badge */}
        <div className="absolute -top-1 right-0 bg-black/80 border border-[#cfb53b] px-1.5 py-0.2 rounded-full text-[8px] font-heading font-bold text-[#cfb53b] shadow-md z-10">
          Lv.{affinityLevel}
        </div>

        {/* Particles */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {particles.map((p) => (
            <span
              key={p.id}
              style={{
                position: "absolute",
                left: `calc(50% + ${p.x}px)`,
                top: `calc(40% + ${p.y}px)`,
                color: p.color,
                fontSize: `${p.size}px`,
                opacity: p.opacity,
                transform: `scale(${p.scale}) rotate(${p.rotation}deg)`,
                textShadow: `0 0 6px ${p.color}`,
              }}
            >
              {p.type}
            </span>
          ))}
        </div>

        {/* Mascot Image */}
        <img
          src={imageSrc}
          alt="Nix Mascot"
          className="w-full h-full object-contain filter drop-shadow-[0_4px_12px_rgba(207,181,59,0.35)] pointer-events-none select-none"
          onError={(e) => {
            e.target.src = "/assets/mascot/nyx_idle.png";
          }}
        />
      </div>

      {/* Keyframe Styles */}
      <style>{`
        @keyframes nyx-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-7px) rotate(1.5deg); }
        }
        .animate-nyx-float {
          animation: nyx-float 3.8s ease-in-out infinite;
        }
        @keyframes nyx-spin {
          0% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.15) rotate(180deg); }
          100% { transform: scale(1) rotate(360deg); }
        }
        .animate-nyx-spin {
          animation: nyx-spin 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        @keyframes nyx-jump {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-22px) scaleY(1.08); }
        }
        .animate-nyx-jump {
          animation: nyx-jump 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        @keyframes nyx-shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-5px); }
          40%, 80% { transform: translateX(5px); }
        }
        .animate-nyx-shake {
          animation: nyx-shake 0.4s ease-in-out forwards;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(5px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
